import { createContext, useContext, useMemo, useRef, useState } from "react";
import { getCartonCount, getCbm, MAX_CARTON_COUNT } from "./utils";

const SplitContext = createContext(null);

export function useSplit() {
  return useContext(SplitContext);
}

export function SplitProvider({ shipment, children }) {
  // Shared allocation state used by both panels.
  const nextTargetId = useRef(2);
  const [targets, setTargets] = useState([{ id: 1, allocations: [] }]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedCartonCounts, setSelectedCartonCounts] = useState({});

  // Source data and lookups.
  const sourceRows = useMemo(
    () =>
      shipment.shipmentInvoices.flatMap((shipmentInvoice) =>
        shipmentInvoice.shipmentInvoiceRows,
      ),
    [shipment],
  );
  const rowById = useMemo(
    () => new Map(sourceRows.map((row) => [row.id, row])),
    [sourceRows],
  );

  // Allocation summary derived from targets.
  const allocatedCartonCounts = useMemo(() => {
    const counts = new Map();
    targets.forEach((target) => {
      target.allocations.forEach((allocation) => {
        counts.set(
          allocation.shipmentInvoiceRowId,
          (counts.get(allocation.shipmentInvoiceRowId) || 0) +
            allocation.cartonCount,
        );
      });
    });
    return counts;
  }, [targets]);
  const remainingCartonCounts = useMemo(
    () =>
      new Map(
        sourceRows.map((row) => {
          const cartonCount = getCartonCount(row);
          return [
            row.id,
            cartonCount === null
              ? null
              : cartonCount - (allocatedCartonCounts.get(row.id) || 0),
          ];
        }),
      ),
    [allocatedCartonCounts, sourceRows],
  );

  // Selection and submission validation.
  const selectedCbm = selectedRowIds.reduce(
    (total, rowId) => {
      const row = rowById.get(rowId);
      return total + getCbm(row, selectedCartonCounts[rowId] || 0);
    },
    0,
  );
  const selectedCartonsAreValid = selectedRowIds.every((rowId) => {
    const cartonCount = selectedCartonCounts[rowId];
    const remainingCartonCount = remainingCartonCounts.get(rowId);
    return (
      Number.isInteger(cartonCount) &&
      cartonCount > 0 &&
      cartonCount <= Math.min(remainingCartonCount, MAX_CARTON_COUNT)
    );
  });
  const canSubmit =
    sourceRows.length > 0 &&
    sourceRows.every((row) => remainingCartonCounts.get(row.id) === 0) &&
    targets.every((target) => target.allocations.length > 0);

  // Actions that change the shared allocation state.
  function addTarget() {
    setTargets((current) => [
      ...current,
      { id: nextTargetId.current++, allocations: [] },
    ]);
  }

  function removeTarget(targetId) {
    setTargets((current) => current.filter((target) => target.id !== targetId));
  }

  function toggleRow(row) {
    const cartonCount = remainingCartonCounts.get(row.id);
    if (selectedRowIds.includes(row.id)) {
      setSelectedRowIds((current) => current.filter((id) => id !== row.id));
    } else {
      setSelectedCartonCounts((counts) => ({
        ...counts,
        [row.id]: Math.min(cartonCount, MAX_CARTON_COUNT),
      }));
      setSelectedRowIds((current) => [...current, row.id]);
    }
  }

  function updateSelectedCartonCount(rowId, cartonCount) {
    setSelectedCartonCounts((current) => ({
      ...current,
      [rowId]: cartonCount,
    }));
  }

  function assignSelectedRows(targetId) {
    if (!selectedRowIds.length || !selectedCartonsAreValid) return;
    setTargets((current) =>
      current.map((target) => {
        if (target.id !== targetId) return target;
        const allocations = [...target.allocations];
        selectedRowIds.forEach((shipmentInvoiceRowId) => {
          const existingIndex = allocations.findIndex(
            (allocation) =>
              allocation.shipmentInvoiceRowId === shipmentInvoiceRowId,
          );
          const cartonCount = selectedCartonCounts[shipmentInvoiceRowId];
          if (existingIndex === -1) {
            allocations.push({ shipmentInvoiceRowId, cartonCount });
          } else {
            allocations[existingIndex] = {
              ...allocations[existingIndex],
              cartonCount: allocations[existingIndex].cartonCount + cartonCount,
            };
          }
        });
        return { ...target, allocations };
      }),
    );
    setSelectedRowIds([]);
  }

  function removeAllocation(targetId, shipmentInvoiceRowId) {
    setTargets((current) =>
      current.map((target) =>
        target.id === targetId
          ? {
              ...target,
              allocations: target.allocations.filter(
                (allocation) =>
                  allocation.shipmentInvoiceRowId !== shipmentInvoiceRowId,
              ),
            }
          : target,
      ),
    );
  }

  return (
    <SplitContext.Provider
      value={{
        shipment,
        rowById,
        remainingCartonCounts,
        targets,
        selectedRowIds,
        selectedCartonCounts,
        selectedCbm,
        selectedCartonsAreValid,
        canSubmit,
        addTarget,
        removeTarget,
        toggleRow,
        updateSelectedCartonCount,
        assignSelectedRows,
        removeAllocation,
      }}
    >
      {children}
    </SplitContext.Provider>
  );
}
